import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, DollarSign, TrendingUp, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Header from "@/components/Header";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function WhatsYourRentWorth() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    property_address: "",
    city: ""
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Track form start event
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'form_start',
        'form_name': 'rent_worth_lead',
        'form_location': 'rent_worth_page'
      });
    }

    try {
      const leadData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        property_address: formData.property_address,
        city: formData.city,
        province: "ON",
        postal_code: "TBD",
        property_type: "residential",
        num_units: 1,
        management_status: "not-managed",
        disclaimer_accepted: true,
        lead_type: "rent_worth" // Special identifier
      };

      const response = await axios.post(`${API}/leads`, leadData);
      
      // Track successful conversion
      if (window.dataLayer && response.data.id) {
        window.dataLayer.push({
          'event': 'form_submission_success',
          'form_name': 'rent_worth_lead',
          'conversion_type': 'rent_worth',
          'lead_id': response.data.id,
          'city': formData.city
        });
      }
      
      toast.success("Thank you! We'll contact you with your rental estimate soon.");
      navigate("/thank-you");
    } catch (error) {
      console.error("Error submitting rent worth lead:", error);
      
      // Track form error
      if (window.dataLayer) {
        window.dataLayer.push({
          'event': 'form_submission_error',
          'form_name': 'rent_worth_lead',
          'error_message': error.response?.data?.detail || 'Submission failed'
        });
      }
      
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header with Full Navigation */}
      <Header variant="full" />

      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            {/* Gold Border Card */}
            <div className="border-4 border-yellow-400 rounded-2xl p-8 sm:p-12 bg-gradient-to-br from-yellow-50 to-amber-50 shadow-2xl relative overflow-hidden">
              {/* Gold Accent Bar */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-yellow-400 to-yellow-600"></div>
              
              <div className="text-center relative">
                <h1 className="text-5xl sm:text-6xl font-bold text-slate-900 mb-6" style={{ letterSpacing: '-2px' }}>
                  What's Your Rent Worth?<sup className="text-3xl">®</sup>
                </h1>
                <p className="text-2xl sm:text-3xl text-slate-700 mb-8 font-semibold">
                  Find Out What Your Property Will Rent For
                </p>
                <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                  Get a free, no-obligation rental market analysis from top property management companies in your area
                </p>
                <Button
                  onClick={() => {
                    const formSection = document.getElementById('rental-form');
                    if (formSection) {
                      formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  size="lg"
                  className="bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-white font-bold text-lg px-10 py-6 shadow-2xl transform hover:scale-105 transition-all duration-300"
                >
                  Find Out Now →
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <DollarSign className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Free Market Analysis</h3>
              <p className="text-slate-600">
                Get accurate rental pricing based on current market conditions in your area
              </p>
            </div>
            <div className="text-center">
              <div className="h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Maximize Your Income</h3>
              <p className="text-slate-600">
                Discover the optimal rental price to maximize your monthly income
              </p>
            </div>
            <div className="text-center">
              <div className="h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Expert Guidance</h3>
              <p className="text-slate-600">
                Connect with professional property managers who can help you succeed
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="rental-form" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">
                Get Your Free Rental Analysis
              </h2>
              <p className="text-xl text-slate-600">
                Fill out the form below and we'll connect you with local experts who will provide a detailed rental market analysis
              </p>
            </div>

            <Card className="bg-slate-50 border-slate-300 shadow-2xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700 font-semibold">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="h-12 text-lg"
                      placeholder="John Smith"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700 font-semibold">Cell Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="h-12 text-lg"
                      placeholder="(416) 555-0123"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-semibold">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="h-12 text-lg"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="property_address" className="text-slate-700 font-semibold">Property Address *</Label>
                    <Input
                      id="property_address"
                      name="property_address"
                      type="text"
                      required
                      value={formData.property_address}
                      onChange={handleChange}
                      className="h-12 text-lg"
                      placeholder="123 Main Street"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-slate-700 font-semibold">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      type="text"
                      required
                      value={formData.city}
                      onChange={handleChange}
                      className="h-12 text-lg"
                      placeholder="Toronto"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-white py-6 text-lg rounded-xl font-semibold shadow-lg shadow-yellow-600/30"
                    disabled={submitting}
                  >
                    {submitting ? "Submitting..." : "Get My Free Rental Analysis"}
                  </Button>

                  <p className="text-sm text-slate-500 text-center">
                    By submitting, you agree to be contacted by property management professionals in your area
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-slate-100">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">How It Works</h2>
            <div className="space-y-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 rounded-full bg-yellow-400 text-yellow-900 flex items-center justify-center font-bold text-lg">
                    1
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Submit Your Property Info</h3>
                  <p className="text-slate-600">
                    Provide basic details about your property using our simple form above
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 rounded-full bg-yellow-400 text-yellow-900 flex items-center justify-center font-bold text-lg">
                    2
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">We Connect You With Experts</h3>
                  <p className="text-slate-600">
                    Local property management professionals will reach out to you directly
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 rounded-full bg-yellow-400 text-yellow-900 flex items-center justify-center font-bold text-lg">
                    3
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Receive Your Rental Analysis</h3>
                  <p className="text-slate-600">
                    Get detailed rental pricing insights and discuss management options if interested
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-white py-12" style={{ backgroundColor: '#1E1E1E' }}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col items-center gap-6">
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6" style={{ color: '#3b82f6' }} />
              <span className="text-xl font-bold">Top Prop</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <button
                onClick={() => navigate("/contact")}
                className="transition-colors duration-300"
                style={{ color: '#9C9C9C' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9C9C9C'}
              >
                Contact Us
              </button>
              <span style={{ color: '#3A3A3A' }}>|</span>
              <button
                onClick={() => navigate("/company-signup")}
                className="transition-colors duration-300"
                style={{ color: '#9C9C9C' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9C9C9C'}
              >
                Join as Property Manager
              </button>
            </div>
            <p className="text-sm" style={{ color: '#9C9C9C' }}>© 2025 Top Prop. All rights reserved.</p>
            <p className="text-xs" style={{ color: '#9C9C9C' }}>What's Your Rent Worth?® is a registered Canadian trademark</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
